import "../../styles/css/custom-font.css";
import "../../styles/css/signup-autofill.css";
import "../../styles/css/social-intents.css";
import * as colors from "../../styles/colors";
import * as documentEventNames from "../../enums/documentEventNames";
import * as pageActions from "../../actions/pageActions";
import * as routeHelpers from "../../lib/routeHelpers";
import appStyles from "../../styles/appStyles";
import AuthenticatedRoute from "../router/AuthenticatedRoute";
import debounce from "lodash.debounce";
import DialogFooter from "../dialog/DialogFooter";
import DialogHeader from "../dialog/DialogHeader";
import DocketLoadable from "../DocketLoadable";
import GoogleAnalytics from "../../components/GoogleAnalytics";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ServiceUnavailableMessage from "../ServiceUnavailableMessage";
import StyledDialog from "../StyledDialog";
import UnauthenticatedRoute from "../router/UnauthenticatedRoute";
import { connect } from "react-redux";
import { css, StyleSheet } from "aphrodite";
import { Route, Switch } from "react-router-dom";
import { ConfigProvider } from "antd";
import { fontFamilyInter } from "../../assets/commonProps";

const styles = StyleSheet.create({
	fullHeight: {
		height: "100vh",
		color: colors.white,
	},
});

const LoadableAuthenticated = DocketLoadable({
	loader: () => import(/* webpackChunkName: "Authenticated" */ "./Authenticated"),
	aStyles: styles.fullHeight,
});

const LoadableDevPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "DevPage" */ "../pages/DevPage"),
	aStyles: styles.fullHeight,
});

const LoadableLoginPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "LoginPage" */ "../pages/LoginPage"),
	aStyles: styles.fullHeight,
});

const LoadableForgotPasswordPage = DocketLoadable({
	loader: () =>
		import(/* webpackChunkName: "ForgotPasswordPage" */ "../pages/ForgotPasswordPage"),
	aStyles: styles.fullHeight,
});

const LoadableResetPasswordPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "ResetPasswordPage" */ "../pages/ResetPasswordPage"),
	aStyles: styles.fullHeight,
});

const LoadableNewGroupPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "NewGroupPage" */ "../pages/NewGroupPage"),
	aStyles: styles.fullHeight,
});

const LoadableNewGroupUserPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "NewGroupUserPage" */ "../pages/NewGroupUserPage"),
	aStyles: styles.fullHeight,
});

const LoadableNewGroupSubmittedPage = DocketLoadable({
	loader: () =>
		import(/* webpackChunkName: "NewGroupSubmittedPage" */ "../pages/NewGroupSubmittedPage"),
	aStyles: styles.fullHeight,
});

const LoadableFreeTrialPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "FreeTrialPage" */ "../pages/FreeTrialPage"),
	aStyles: styles.fullHeight,
});

const LoadableRequestAccessPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "RequestAccessPage" */ "../pages/RequestAccessPage"),
	aStyles: styles.fullHeight,
});

const LoadableNewIndividualUserPage = DocketLoadable({
	loader: () =>
		import(/* webpackChunkName: "NewIndividualUserPage" */ "../pages/NewIndividualUserPage"),
	aStyles: styles.fullHeight,
});

const LoadableNewUnlimitedUserPage = DocketLoadable({
	loader: () =>
		import(/* webpackChunkName: "NewUnlimitedUserPage" */ "../pages/NewUnlimitedUserPage"),
	aStyles: styles.fullHeight,
});

const LoadableWelcomePage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "WelcomePage" */ "../pages/WelcomePage"),
	aStyles: styles.fullHeight,
});

const LoadableErrorPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "ErrorPage" */ "../pages/ErrorPage"),
	aStyles: styles.fullHeight,
});

const LoadableNotFoundPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "NotFoundPage" */ "../pages/NotFoundPage"),
	aStyles: styles.fullHeight,
});

const LoadableUnauthorizedPage = DocketLoadable({
	loader: () => import(/* webpackChunkName: "UnauthorizedPage" */ "../pages/UnauthorizedPage"),
	aStyles: styles.fullHeight,
});

const LoadableBinderPrintPreview = DocketLoadable({
	loader: () =>
		import(/* webpackChunkName: "BinderPrintPreview" */ "../pages/BinderPrintPreview"),
	aStyles: styles.fullHeight,
});

const strings = {
	contactUs: "contact us",
	dismiss: "Dismiss",
	errorOccurred1: "An error has occurred while processing your request.",
	errorOccurred2: "Try again, but if the issue persists, please ",
	hereToHelp: ". We're here to help.",
	oops: "Oops!",
	temporarilyDown: "Docket Navigator is temporarily down",
};

class App extends PureComponent {
	constructor(props) {
		super(props);
		this._debouncedResize = debounce(this._handleResize, 300);
	}

	static propTypes = {
		errorDialogOpen: PropTypes.bool.isRequired,
		pageSetErrorDialogOpen: PropTypes.func.isRequired,
		pageSetServiceUnavailable: PropTypes.func.isRequired,
		pageSetWindowDimensions: PropTypes.func.isRequired,
		serviceUnavailable: PropTypes.bool.isRequired,
	};

	componentDidMount() {
		this._handleResize();
		window.addEventListener(documentEventNames.RESIZE, this._debouncedResize);
	}

	componentWillUnmount() {
		window.removeEventListener(documentEventNames.RESIZE, this._debouncedResize);
	}

	_handleResize = () => this.props.pageSetWindowDimensions(window.innerHeight, window.innerWidth);
	_closeError = () => this.props.pageSetErrorDialogOpen(false);
	_closeServiceUnavailable = () => this.props.pageSetServiceUnavailable(false);

	_renderDevRoute = () => {
		return <Route component={LoadableDevPage} exact={true} path={routeHelpers.dev()} />;
	};

	render() {
		return (
			<ConfigProvider
				theme={{
					token: {
						borderRadiusLG: "4px",
						fontFamily:
							"'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
					},
					components: {
						Card: {
							boxShadowTertiary:
								"rgba(0, 0, 0, 0.05) 0px 1px 3px, rgba(0, 0, 0, 0.05) 0px 10px 15px -5px, rgba(0, 0, 0, 0.04) 0px 7px 7px -5px",
						},
						Collapse: {
							fontFamily: fontFamilyInter,
						},
					},
				}}
			>
				<div>
					{typeof ga !== undefined ? <Route component={GoogleAnalytics} /> : null}

					<Switch>
						<UnauthenticatedRoute
							exact={true}
							path={routeHelpers.login()}
							component={LoadableLoginPage}
						/>

						<UnauthenticatedRoute
							exact={true}
							path={routeHelpers.forgotPassword()}
							component={LoadableForgotPasswordPage}
						/>

						<UnauthenticatedRoute
							exact={true}
							path={routeHelpers.resetPassword()}
							component={LoadableResetPasswordPage}
						/>

						<UnauthenticatedRoute
							component={LoadableNewGroupPage}
							exact={true}
							path={routeHelpers.newGroup()}
						/>

						<UnauthenticatedRoute
							component={LoadableNewGroupSubmittedPage}
							exact={true}
							path={routeHelpers.newGroupSubmissionConfirmation()}
						/>

						<UnauthenticatedRoute
							component={LoadableFreeTrialPage}
							exact={true}
							path={routeHelpers.freeTrial()}
						/>

						<UnauthenticatedRoute
							component={LoadableRequestAccessPage}
							exact={true}
							path={routeHelpers.requestAccess()}
						/>

						<UnauthenticatedRoute
							component={LoadableNewGroupUserPage}
							exact={true}
							path={routeHelpers.newGroupUser()}
						/>

						<UnauthenticatedRoute
							component={LoadableNewUnlimitedUserPage}
							exact={true}
							path={routeHelpers.newUnlimitedGroupUser()}
						/>

						<Route
							component={LoadableNewIndividualUserPage}
							exact={true}
							path={routeHelpers.newIndividualUser()}
						/>

						<Route
							component={LoadableErrorPage}
							exact={true}
							path={routeHelpers.error()}
						/>

						<Route
							component={LoadableNotFoundPage}
							exact={true}
							path={routeHelpers.notFound()}
						/>

						<Route
							component={LoadableUnauthorizedPage}
							exact={true}
							path={routeHelpers.unauthorized()}
						/>

						<AuthenticatedRoute
							component={LoadableWelcomePage}
							exact={true}
							path={routeHelpers.welcome()}
						/>

						{__DEV__ ? this._renderDevRoute() : null}

						<AuthenticatedRoute
							component={LoadableBinderPrintPreview}
							exact={true}
							path="/:libraryName/:binderType/:binderId/print"
						/>

						<AuthenticatedRoute component={LoadableAuthenticated} />
					</Switch>

					<StyledDialog
						open={this.props.errorDialogOpen}
						title={<DialogHeader title={strings.oops} handleClose={this._closeError} />}
					>
						<span className={css(appStyles.baseFont)}>
							{strings.errorOccurred1} {strings.errorOccurred2}
							<a href="mailto:contact@docketnavigator.com">{strings.contactUs}</a>
							{strings.hereToHelp}
						</span>
						<DialogFooter
							handleSubmit={this._closeError}
							submitText={strings.dismiss}
						/>
					</StyledDialog>

					<StyledDialog
						open={this.props.serviceUnavailable}
						title={
							<DialogHeader
								title={strings.temporarilyDown}
								handleClose={this._closeServiceUnavailable}
							/>
						}
					>
						<ServiceUnavailableMessage />
						<DialogFooter
							handleSubmit={this._closeServiceUnavailable}
							submitText={strings.dismiss}
						/>
					</StyledDialog>
				</div>
			</ConfigProvider>
		);
	}
}

const mapStateToProps = state => ({
	errorDialogOpen: state.page.errorDialogOpen,
	serviceUnavailable: state.page.serviceUnavailable,
});

export default connect(
	mapStateToProps,
	{
		pageSetErrorDialogOpen: pageActions.pageSetErrorDialogOpen,
		pageSetServiceUnavailable: pageActions.pageSetServiceUnavailable,
		pageSetWindowDimensions: pageActions.pageSetWindowDimensions,
	},
)(App);
